import { useMemo } from "react";
import { Column } from "primereact/column";
import localeText from "../../../../locale";
import AdminService from "../../../../service/AdminService";
import { buildSearchText, getToken } from "../../utils/adminUtils";
import CatalogTable from "../../components/list/CatalogTable";
import useReport from "./hooks/useReport";
import ReportToolbar from "./components/ReportToolbar";
import ReportSummary from "./components/ReportSummary";
import InvoicingControl from "./components/InvoicingControl";
import InvoiceBadge from "./components/InvoiceBadge";
import { formatMoney, formatReportDate, sumBy, toReportRows, toTimestamp } from "./reportUtils";
import "../AdminHome/AdminHome.css";
import "./AdminReports.css";

const PLAN_TYPE_KEYS = { FIRST: "initialSub", RENEW: "annualRenewal", TRIAL: "test" };

const planTypeLabel = (value) => (PLAN_TYPE_KEYS[value] ? localeText(PLAN_TYPE_KEYS[value]) : value);

const loadPaidSubscriptions = async (dateBegin, dateEnd) =>
  toReportRows(await new AdminService().getPaidSubscriptionsReport(getToken(), dateBegin, dateEnd));

const loadPaidSubscriptionsPdf = (dateBegin, dateEnd) =>
  new AdminService().getPaidSubscriptionsReportPdf(getToken(), dateBegin, dateEnd);

const AdminPaidSubscriptionsReport = ({ canManageInvoicing = false }) => {
  const report = useReport({
    load: loadPaidSubscriptions,
    loadPdf: loadPaidSubscriptionsPdf,
    pdfFileName: "paid-subscriptions-report",
  });
  const { rows: rawRows, loading, loadError, reload } = report;

  const rows = useMemo(
    () =>
      rawRows.map((row, index) => ({
        ...row,
        id: row.id ?? `row-${index}`,
        fullName: `${row.firstname || ""} ${row.surname || ""}`.trim(),
        planTypeLabel: planTypeLabel(row.typePaymentPlan),
        dateValue: toTimestamp(row.date),
        amountValue: Number(row.amountPaid) || 0,
        searchText: buildSearchText([
          row.firstname,
          row.surname,
          row.partitaivaOrCodicefiscale,
          row.address,
          row.paymentMethod,
        ]),
      })),
    [rawRows],
  );

  const countByType = (type) => rows.filter((row) => row.typePaymentPlan === type).length;

  const summary = [
    { label: localeText("adminReportsSubscriptionsCount"), value: rows.length.toLocaleString() },
    { label: localeText("adminReportsTotalPaid"), value: formatMoney(sumBy(rows, (row) => row.amountPaid)) },
    { label: localeText("initialSub"), value: countByType("FIRST").toLocaleString() },
    { label: localeText("annualRenewal"), value: countByType("RENEW").toLocaleString() },
  ];

  return (
    <div className="admin-list admin-report">
      <ReportToolbar report={report} />
      {canManageInvoicing && <InvoicingControl />}
      <ReportSummary items={summary} loading={loading} />

      <CatalogTable
        rows={rows}
        loading={loading}
        loadError={loadError}
        onReload={reload}
        searchPlaceholder={localeText("adminReportsSearchSubscriptions")}
        sortField="dateValue"
        sortOrder={-1}
        minWidth="64rem"
      >
        <Column
          header={localeText("name")}
          field="fullName"
          body={(row) => (
            <div className="admin-stack">
              <span className="admin-strong">{row.fullName || "—"}</span>
              {row.partitaivaOrCodicefiscale && (
                <span className="admin-muted">
                  {localeText("taxId")}: {row.partitaivaOrCodicefiscale}
                </span>
              )}
              {row.address && (
                <span className="admin-muted admin-report__address">{row.address}</span>
              )}
            </div>
          )}
          sortable
          style={{ minWidth: "16rem" }}
        />
        <Column
          header={localeText("date")}
          field="dateValue"
          body={(row) => formatReportDate(row.date)}
          sortable
        />
        <Column header={localeText("planType")} field="planTypeLabel" sortable />
        <Column
          header={localeText("paymentMethod")}
          field="paymentMethod"
          body={(row) => row.paymentMethod || "—"}
          sortable
        />
        <Column
          header={localeText("amount")}
          field="amountValue"
          body={(row) => <span className="admin-num admin-strong">{formatMoney(row.amountPaid)}</span>}
          sortable
        />
        <Column
          header={localeText("invoiceStatus")}
          field="invoiceStatus"
          body={(row) => (
            <InvoiceBadge
              status={row.invoiceStatus}
              number={row.invoiceNumber}
              year={row.invoiceYear}
              errorMessage={row.invoiceErrorMessage}
            />
          )}
          sortable
        />
      </CatalogTable>
    </div>
  );
};

export default AdminPaidSubscriptionsReport;
